<template>
    <div class="card bt-form-page">
        <PageHeader
            :title="pageTitle"
            :subtitle="pageSubtitle"
            @onClickCloseButton="navigateToList"
        />

        <div class="bt-form-body" :class="{ 'is-loading': pageLoading }">
            <FormSection title="Budget Allocation Details" icon="fa-solid fa-circle-info">
                <div class="row g-1">
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Master Budget Line <span class="bt-required">*</span></label>
                        <v-select
                            v-model="formData.project_master_id"
                            placeholder="Select master budget line"
                            :options="budgetLines"
                            label="name"
                            :reduce="option => option.id"
                            :disabled="!! budgetId"
                            :class="{ 'is-invalid-select': fieldError('project_master_id') }"
                        />
                        <span v-if="fieldError('project_master_id')" class="invalid-feedback d-block">{{ fieldError('project_master_id') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Interval</label>
                        <input type="text" class="form-control bt-input" :value="intervalName" disabled>
                    </div>
                    <div class="col-6 col-lg-2">
                        <label class="bt-label">Start Date</label>
                        <input type="date" class="form-control bt-input" :value="selectedLine.start_date" disabled>
                    </div>
                    <div class="col-6 col-lg-2">
                        <label class="bt-label">End Date</label>
                        <input type="date" class="form-control bt-input" :value="selectedLine.end_date" disabled>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Budget Allocation Name <span class="bt-required">*</span></label>
                        <input
                            v-model="formData.name"
                            type="text"
                            class="form-control bt-input"
                            :class="{ 'is-invalid': fieldError('name') }"
                            placeholder="Enter budget allocation name"
                        >
                        <span v-if="fieldError('name')" class="invalid-feedback d-block">{{ fieldError('name') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Budget Allocation Code</label>
                        <input v-model="formData.code" type="text" class="form-control bt-input" placeholder="Enter budget allocation code">
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Create Date <span class="bt-required">*</span></label>
                        <input
                            v-model="formData.create_date"
                            type="date"
                            class="form-control bt-input"
                            :class="{ 'is-invalid': fieldError('create_date') }"
                        >
                        <span v-if="fieldError('create_date')" class="invalid-feedback d-block">{{ fieldError('create_date') }}</span>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Budget Type <span class="bt-required">*</span></label>
                        <v-select
                            v-model="budgetType"
                            placeholder="Select budget type"
                            :options="budgetTypeOptions"
                            label="name"
                            :reduce="option => option.id"
                            :clearable="false"
                        />
                    </div>
                    <div v-if="budgetType === 'business_unit'" class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Business Unit <span class="bt-required">*</span></label>
                        <v-select
                            v-model="formData.business_id"
                            placeholder="Select business unit"
                            :options="businesses"
                            label="name"
                            :reduce="option => option.id"
                            :class="{ 'is-invalid-select': fieldError('business_id') }"
                        />
                        <span v-if="fieldError('business_id')" class="invalid-feedback d-block">{{ fieldError('business_id') }}</span>
                    </div>
                    <div v-if="budgetType === 'cost_centre'" class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Cost Center <span class="bt-required">*</span></label>
                        <v-select
                            v-model="formData.cost_centre_id"
                            placeholder="Select cost center"
                            :options="costCentres"
                            label="name"
                            :reduce="option => option.id"
                            :class="{ 'is-invalid-select': fieldError('cost_centre_id') }"
                        />
                        <span v-if="fieldError('cost_centre_id')" class="invalid-feedback d-block">{{ fieldError('cost_centre_id') }}</span>
                    </div>
                    <div v-if="budgetType === 'party'" class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Party <span class="bt-required">*</span></label>
                        <AsyncSelect
                            v-model="formData.party_id"
                            placeholder="Select party"
                            :api-service="fetchParties"
                            label="name"
                            :reduce="option => option.id"
                            :additional-option="partyOptions"
                            :input-class="partyInputClass"
                        />
                        <span v-if="fieldError('party_id')" class="invalid-feedback d-block">{{ fieldError('party_id') }}</span>
                    </div>
                    <div v-if="budgetType === 'project'" class="col-12 col-sm-6 col-lg-4">
                        <label class="bt-label">Project <span class="bt-required">*</span></label>
                        <AsyncSelect
                            v-model="formData.project_id"
                            placeholder="Select project"
                            :api-service="fetchProjects"
                            :additional-query="{ status: 'active' }"
                            label="name"
                            :reduce="option => option.id"
                            :additional-option="projectOptions"
                            :input-class="projectInputClass"
                        />
                        <span v-if="fieldError('project_id')" class="invalid-feedback d-block">{{ fieldError('project_id') }}</span>
                    </div>
                </div>
            </FormSection>

            <FormSection title="Budget Heads" icon="fa-solid fa-sitemap">
                <template v-if="budgetId">
                    <VersionBar
                        :versions="versions"
                        :selectedId="selectedVersionId"
                        :workingId="workingVersionId"
                        :busy="versionBusy"
                        @select="onSelectVersion"
                        @action="onVersionAction"
                        @revise="onRevise"
                        @history="historyOpen = true"
                    />

                    <div class="bt-tree" :class="{ 'is-loading': treeLoading }">
                        <AccountTree
                            :key="tab + treeKey"
                            :groupData="projectAccountList.group"
                            :endpointData="projectAccountList.endpoint"
                            :accountType="tab"
                            :tabs="tabs"
                            :isExporting="headSheet.isExporting"
                            :fetchTree="fetchTreeChildren"
                            :canImport="canImport"
                            @handleTabChange="handleTabChange"
                            @handleRootPagination="getAccountTree"
                            @handleExport="headSheet.exportSheet()"
                            @handleImport="openHeadSheetModal"
                        />
                    </div>
                </template>

                <EmptyState
                    v-if="! budgetId"
                    compact
                    icon="fa-solid fa-lock"
                    title="Save the budget allocation first"
                    message="Item and Account lines can be entered once the budget allocation has been saved as V1 draft."
                />
            </FormSection>
        </div>

        <div class="bt-form-footer">
            <button type="button" class="btn bt-btn bt-btn-ghost" :disabled="isSaving" @click="navigateToList">Cancel</button>
            <button type="button" class="btn bt-btn bt-btn-primary" :disabled="isSaving || pageLoading" @click="handleSubmit">
                <span v-if="isSaving" class="spinner-border spinner-border-sm"></span>
                <span>Save</span>
            </button>
        </div>

        <BudgetGeneralModal
            ref="budgetGeneralModal"
            :key="budgetGeneralForm.project_account_id"
            :form="budgetGeneralForm"
            :accountType="tab"
            :readonly="! canEditLines"
            :saving="lineSaving"
            @onBusinessGeneralSave="saveBudgetLine"
            @onResetBreakdown="resetBreakdown"
        />

        <BudgetHeadSheetModal
            ref="budgetHeadSheetModal"
            :sheet="headSheet"
        />

        <VersionHistoryModal
            :isOpen="historyOpen"
            :versions="versions"
            :exportingVersionId="exportingVersionId"
            @close="historyOpen = false"
            @export="onExportVersion"
        />
    </div>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import FormSection from '@/components/atom/FormSection.vue'
import EmptyState from '@/components/atom/EmptyState.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import AccountTree from '@/components/molecule/procurement/budget/AccountTree'
import BudgetGeneralModal from '@/components/molecule/procurement/budget/BudgetGeneralModal'
import BudgetHeadSheetModal from '@/components/molecule/procurement/budget/head-sheet/BudgetHeadSheetModal.vue'
import VersionBar from '@/components/molecule/procurement/budget-tracker/VersionBar.vue'
import VersionHistoryModal from '@/components/molecule/procurement/budget-tracker/VersionHistoryModal.vue'
import handleBudgetTracker from '@/services/modules/procurement/budgetTracker'
import handleProjects from '@/services/modules/procurement/project'
import handleDropdowns from '@/services/modules/dropdowns'
import useBudgetHeadSheet from '@/composables/useBudgetHeadSheet'
import useBudgetVersionExport from '@/composables/useBudgetVersionExport'
import useAutoRefresh from '@/composables/useAutoRefresh'
import { getApiErrorMessage } from '@/services/utils/global.js'

const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')
const emitter = inject('emitter')
const { fetchParties, fetchProjects } = handleProjects()
const { fetchDropdownList } = handleDropdowns()
const {
    fetchBudgetLines,
    createTrackerBudget,
    updateTrackerBudget,
    fetchTrackerBudget,
    fetchTrackerTree,
    fetchTrackerLine,
    storeTrackerLine,
    fetchTrackerPeriods,
    reviseTrackerBudget,
    updateVersionStatus,
} = handleBudgetTracker()
const { exportingVersionId, exportVersion } = useBudgetVersionExport(() => $route.params.companyId)

const emptyForm = () => ({
    project_master_id: null,
    name: '',
    code: '',
    create_date: '',
    business_id: null,
    cost_centre_id: null,
    party_id: null,
    party_name: '',
    project_id: null
})

const dimensionKeys = {
    business_unit: 'business_id',
    cost_centre: 'cost_centre_id',
    party: 'party_id',
    project: 'project_id'
}

const dimensionMessages = {
    business_id: 'Please select a business unit.',
    cost_centre_id: 'Please select a cost center.',
    party_id: 'Please select a party.',
    project_id: 'Please select a project.'
}

const emptyLine = () => ({
    budget_master_id: '',
    project_account_id: '',
    project_account_name: '',
    quantity: '',
    rate: '',
    amount: '',
    fill_type: 'fill'
})

const budgetGeneralModal = ref(null)
const budgetHeadSheetModal = ref(null)
const pageLoading = ref(false)
const treeLoading = ref(false)
const isSaving = ref(false)
const lineSaving = ref(false)
const versionBusy = ref(false)
const historyOpen = ref(false)
const errors = ref({})
const tab = ref('account')
const treeKey = ref(0)
const homeContent = ref({})
const businesses = ref([])
const costCentres = ref([])
const budgetLines = ref([])
const versions = ref([])
const selectedVersionId = ref(null)
const workingVersionId = ref(null)
const formData = ref(emptyForm())
const budgetType = ref('general')
const savedProject = ref({ id: null, name: '' })
const budgetGeneralForm = ref(emptyLine())
const projectAccountList = ref({ group: [], endpoint: {} })

const budgetId = computed(() => $route.params.budgetId || '')

const companyQuery = computed(() => `?company_id=${$route.params.companyId}`)

const pageTitle = computed(() => budgetId.value ? 'Edit Budget Allocation' : 'Add Budget Allocation')

const pageSubtitle = computed(() => budgetId.value ? 'Change lines through versions; an approved budget is changed by a revised budget' : 'Pick a master budget line and save to start V1 as a draft')

const selectedLine = computed(() => {
    const found = budgetLines.value.find(line => line.id === formData.value.project_master_id)
    return found || {}
})

const intervalName = computed(() => {
    const intervals = homeContent.value.project_intervals || {}
    return intervals[selectedLine.value.interval] || ''
})

const toOptions = (source) => {
    if (! source) return []
    return Object.keys(source).map(id => ({ id, name: source[id] }))
}

const budgetTypeOptions = computed(() => toOptions(homeContent.value.budget_types))

const budgetTypeOf = (data) => {
    const found = Object.keys(dimensionKeys).find(type => !! data[dimensionKeys[type]])
    return found || 'general'
}

const clearOtherDimensions = (type) => {
    Object.keys(dimensionKeys).filter(key => key !== type).forEach(key => {
        formData.value[dimensionKeys[key]] = null
    })
    if (type !== 'party') formData.value.party_name = ''
}

watch(budgetType, clearOtherDimensions)

const partyInputClass = computed(() => {
    if (! fieldError('party_id')) return ''
    return 'is-invalid-select'
})

const partyOptions = computed(() => {
    if (! formData.value.party_id || ! formData.value.party_name) return []
    return [{ id: formData.value.party_id, name: formData.value.party_name }]
})

const projectInputClass = computed(() => {
    if (! fieldError('project_id')) return ''
    return 'is-invalid-select'
})

const projectOptions = computed(() => {
    const saved = savedProject.value
    if (! saved.id || saved.id !== formData.value.project_id) return []
    return [saved]
})

const tabs = computed(() => {
    const types = homeContent.value.budget_head_type
    if (! types) return []
    return Object.keys(types).map(key => ({ id: key, name: types[key] }))
})

const currentTab = computed(() => tabs.value.find(item => item.id === tab.value) || tabs.value[0])

const workingVersion = computed(() => versions.value.find(version => version.id === workingVersionId.value))

const workingLabel = computed(() => workingVersion.value ? `V${workingVersion.value.version_no}` : '')

const isWorkingSelected = computed(() => selectedVersionId.value === workingVersionId.value)

const workingStatus = computed(() => workingVersion.value ? workingVersion.value.status : '')

const canEditLines = computed(() => isWorkingSelected.value && workingStatus.value === 'draft')

const canImport = computed(() => isWorkingSelected.value && ['draft', 'active'].includes(workingStatus.value))

const lockedMessage = computed(() => {
    if (! isWorkingSelected.value) return 'This is an old version. It can be viewed and exported only.'
    if (workingStatus.value === 'pending') return `${workingLabel.value} is waiting for approval and cannot be changed.`
    if (workingStatus.value === 'rejected') return `${workingLabel.value} was rejected. Reopen it to make changes.`
    return 'This budget is approved. Create a revised budget to change it.'
})

const tabQuery = () => {
    return companyQuery.value + `&account_type=${tab.value}`
}

const sheetQuery = () => {
    if (isWorkingSelected.value || ! selectedVersionId.value) return tabQuery()
    return tabQuery() + `&version_id=${selectedVersionId.value}`
}

const versionQuery = () => selectedVersionId.value ? `&version_id=${selectedVersionId.value}` : ''

const headSheet = reactive(useBudgetHeadSheet(
    () => budgetId.value,
    () => sheetQuery(),
    () => formData.value.name,
    () => currentTab.value,
    () => refreshAfterChange()
))

const fieldError = (key) => errors.value[key] || ''

const validate = () => {
    const found = {}
    if (! formData.value.project_master_id) found.project_master_id = 'Please select a master budget line.'
    if (! formData.value.name) found.name = 'Budget allocation name is required.'
    if (! formData.value.create_date) found.create_date = 'Create date is required.'
    const dimensionKey = dimensionKeys[budgetType.value]
    if (dimensionKey && ! formData.value[dimensionKey]) found[dimensionKey] = dimensionMessages[dimensionKey]
    errors.value = found
    return Object.keys(found).length === 0
}

const navigateToList = () => {
    const params = Object.assign({}, $route.params)
    delete params.budgetId
    $router.push({ name: 'budget-tracker', params, query: $route.query })
}

let versionLoadNo = 0

const setVersions = (data) => {
    versions.value = data.versions
    workingVersionId.value = data.working_version_id
    selectedVersionId.value = data.working_version_id
    formData.value.status = data.status
}

const getBudget = async (id) => {
    versionLoadNo++
    const res = await fetchTrackerBudget(id, companyQuery.value)
    if (! res.status) return showError(res.message)
    formData.value = Object.assign(emptyForm(), res.data)
    savedProject.value = { id: res.data.project_id, name: res.data.project_name }
    budgetType.value = budgetTypeOf(res.data)
    setVersions(res.data)
}

const loadVersions = async () => {
    versionLoadNo++
    const res = await fetchTrackerBudget(budgetId.value, companyQuery.value)
    if (! res.status) return showError(res.message)
    setVersions(res.data)
}

const versionSignature = (list) => list.map(version => `${version.id}:${version.status}`).join(',')

const followWorkingVersion = (previousWorkingId, nextWorkingId) => {
    if (selectedVersionId.value !== previousWorkingId) return
    selectedVersionId.value = nextWorkingId
}

const applyVersions = async (data) => {
    const changed = versionSignature(data.versions) !== versionSignature(versions.value)
    const previousWorkingId = workingVersionId.value
    versions.value = data.versions
    workingVersionId.value = data.working_version_id
    formData.value.status = data.status
    followWorkingVersion(previousWorkingId, data.working_version_id)
    if (! changed) return
    await getAccountTree()
    treeKey.value++
}

const refreshVersions = async () => {
    if (! budgetId.value) return
    const res = await fetchTrackerBudget(budgetId.value, companyQuery.value)
    if (! res.status) return
    await applyVersions(res.data)
}

const isBusy = () => versionBusy.value || isSaving.value || lineSaving.value

const refreshSilently = async () => {
    if (! budgetId.value || isBusy()) return
    const startedAt = versionLoadNo
    const res = await fetchTrackerBudget(budgetId.value, companyQuery.value)
    if (isBusy() || startedAt !== versionLoadNo) return
    if (! res.status) return
    await applyVersions(res.data)
}

useAutoRefresh(refreshSilently, () => workingStatus.value === 'pending')

const getAccountTree = async (page = 1) => {
    if (! budgetId.value) return
    treeLoading.value = true
    try {
        const res = await fetchTrackerTree(budgetId.value, tabQuery() + versionQuery() + `&page=${page}&offset=10`)
        if (! res.status) return showError(res.message)
        projectAccountList.value = { group: res.data.group, endpoint: res.data.endpoint }
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        treeLoading.value = false
    }
}

const fetchTreeChildren = (query) => fetchTrackerTree(budgetId.value, query + versionQuery())

const refreshAfterChange = async () => {
    await loadVersions()
    await getAccountTree()
    treeKey.value++
}

const saveHeader = () => {
    if (budgetId.value) return updateTrackerBudget(budgetId.value, formData.value, companyQuery.value)
    return createTrackerBudget(formData.value, companyQuery.value)
}

const afterHeaderSaved = async (res) => {
    showSuccess(res.message)
    if (budgetId.value) return
    await $router.replace({
        name: 'budget-tracker-edit',
        params: Object.assign({}, $route.params, { budgetId: res.data.id }),
        query: $route.query
    })
    await getBudget(res.data.id)
    await getAccountTree()
}

const handleSubmit = async () => {
    if (! validate()) return
    if (! selectedLine.value.interval) return showError('Please update the master budget line with an interval first.')
    isSaving.value = true
    try {
        const res = await saveHeader()
        if (! res.status) return showError(res.message)
        await afterHeaderSaved(res)
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        isSaving.value = false
    }
}

const handleTabChange = (tabName) => {
    tab.value = tabName
    getAccountTree()
}

const onSelectVersion = (versionId) => {
    selectedVersionId.value = versionId
    getAccountTree()
    treeKey.value++
}

const onActionRefused = async (message) => {
    showError(message)
    await refreshVersions()
}

const runVersionRequest = async (request) => {
    versionBusy.value = true
    try {
        const res = await request()
        if (! res.status) return await onActionRefused(res.message)
        showSuccess(res.message)
        await refreshAfterChange()
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        versionBusy.value = false
    }
}

const onVersionAction = (action) => {
    runVersionRequest(() => updateVersionStatus(budgetId.value, workingVersionId.value, { action }, companyQuery.value))
}

const onRevise = () => {
    runVersionRequest(() => reviseTrackerBudget(budgetId.value, {}, companyQuery.value))
}

const onExportVersion = (version) => {
    exportVersion(budgetId.value, formData.value.name, version)
}

const openHeadSheetModal = () => {
    if (! canImport.value) return showError(lockedMessage.value)
    budgetHeadSheetModal.value.openModal()
}

const openBudgetLine = async (headId) => {
    budgetGeneralForm.value = emptyLine()
    try {
        const res = await fetchTrackerLine(budgetId.value, companyQuery.value + `&project_account_id=${headId}` + versionQuery())
        if (! res.status) return showError(res.message)
        budgetGeneralForm.value = Object.assign({}, res.data)
        await nextTick()
        budgetGeneralModal.value.openModal()
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const saveBudgetLine = async () => {
    if (lineSaving.value) return
    if (! canEditLines.value) return showError(lockedMessage.value)
    lineSaving.value = true
    try {
        const res = await storeTrackerLine(budgetId.value, budgetGeneralForm.value, companyQuery.value)
        if (! res.status) return await onActionRefused(res.message)
        showSuccess(res.message)
        budgetGeneralModal.value.closeModal()
        await refreshAfterChange()
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        lineSaving.value = false
    }
}

const resetBreakdown = async () => {
    const res = await fetchTrackerPeriods(budgetId.value, companyQuery.value)
    if (! res.status) return showError(res.message)
    budgetGeneralForm.value.breakdown = res.data
    budgetGeneralForm.value.breakdown_qty = Object.assign({}, res.data)
}

const getDropdowns = async () => {
    const res = await fetchDropdownList(companyQuery.value + '&is_business=1&is_cost_centre=1&is_home=1')
    if (! res.status || ! res.data) return
    homeContent.value = res.data.home_data
    businesses.value = res.data.business_list
    costCentres.value = res.data.cost_centre_list
}

const getBudgetLines = async () => {
    const res = await fetchBudgetLines(companyQuery.value)
    if (! res.status) return
    budgetLines.value = res.data
}

const onBudgetGeneralEdit = ({ data }) => {
    openBudgetLine(data.data.id)
}

onMounted(async () => {
    pageLoading.value = true
    try {
        await Promise.all([getDropdowns(), getBudgetLines()])
        if (! budgetId.value) return
        await getBudget(budgetId.value)
        await getAccountTree()
    } catch (err) {
        showError(getApiErrorMessage(err))
    } finally {
        pageLoading.value = false
    }
})

emitter.off('onBudgetGeneralEdit')
emitter.on('onBudgetGeneralEdit', onBudgetGeneralEdit)

onUnmounted(() => {
    emitter.off('onBudgetGeneralEdit', onBudgetGeneralEdit)
})
</script>

<style scoped>
.bt-form-page {
    min-height: calc(100vh - 158px);
}
.bt-form-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 14px 16px;
    transition: opacity .15s ease;
}
.bt-form-body.is-loading {
    opacity: .55;
    pointer-events: none;
}
.bt-label {
    display: block;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #344054;
}
.bt-required {
    color: #d92d20;
}
.bt-input {
    height: 40px;
    border-radius: 8px;
    border-color: #d0d5dd;
    font-size: 14px;
}
.bt-form-body :deep(.vs__dropdown-toggle) {
    min-height: 40px;
    border-radius: 8px;
    border-color: #d0d5dd;
    font-size: 14px;
}
.bt-form-body :deep(.is-invalid-select .vs__dropdown-toggle) {
    border-color: #ea5455;
}
.bt-form-body .invalid-feedback {
    font-size: 13px;
}
.bt-tree {
    margin-top: 12px;
    font-size: 14px;
    transition: opacity .15s ease;
}
.bt-tree.is-loading {
    opacity: .5;
}
.bt-form-footer {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-top: 1px solid #eaecf0;
    border-radius: 0 0 .428rem .428rem;
    background: #fff;
    box-shadow: 0 -4px 12px rgba(16, 24, 40, .05);
}
.bt-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    min-width: 96px;
    padding: 0 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
}
.bt-btn:disabled {
    opacity: .6;
    cursor: not-allowed;
}
.bt-btn-primary {
    background: #7367f0;
    border: 1px solid #7367f0;
    color: #fff;
}
.bt-btn-primary:hover:not(:disabled) {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
.bt-btn-ghost {
    background: #fff;
    border: 1px solid #e4e7ec;
    color: #475467;
}
.bt-btn-ghost:hover:not(:disabled) {
    background: #f9fafb;
    color: #101828;
}
</style>
